import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { APIResponse } from '../../models/APIResponse';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ContaminantComposition, ContaminationFlashedCalcData, ContaminationFlashedData, DeadLiveCalcData, DeContaminationChart, DecontaminationCalculation, DecontaminationInputModel, DecontaminationResult, DecontaminationSaveData, StatusParamsDecont, Vw_DecontaminationContaminatedData, Vw_DecontaminationPropertiesData } from '../models/decontamination';
import { BehaviorSubject, map } from 'rxjs';
import { Chart, ChartDataset, ChartItem, ChartType, ScatterDataPoint } from 'chart.js';
import 'chart.js';
import type { Plugin as ChartJsPlugin, FontSpec } from 'chart.js';
import { toFont } from 'chart.js/helpers'
import { CVD_CONSTANTS as CC, PARAFFIN_CONSTANTS as PC, PVT_CONSTANTS as PVC, reportConstants as RPC } from '../../common/constants';
import { API_ENDPOINTS as AE, decontaminationConstants as DC, RECOMBINATION_CONSTANTS as RC } from '../../common/experiment-constants';
import { NUMBERS_CONSTANTS as NC} from '../../common/numbers-constants';
import { apiV1 } from '../../common/texts-constants';

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    customLegendAndTitle?: {
      titleText?: string;
    };
    __customLegendType__?: TType;
  }
}

type ScatterPoint = ScatterDataPoint | { x: number; y: number; compound?: string };
type ScatterDataset = ChartDataset<'scatter', ScatterPoint[]>;

export interface DecontaminationViewState {
  decontaminationInputModel: DecontaminationInputModel;
  decontaminationCalculation: DecontaminationCalculation;
  decontaminationResult: DecontaminationResult;
  showResult: boolean;
  dataLoaded: boolean;
}

export interface DecontaminationCalculationViewState {
  contaminantPropertiesCalcData: DecontaminationCalculation['contaminantPropertiesCalcData'];
  contaminationFlashedCalcData: ContaminationFlashedCalcData[];
  contaminantDeadOilCalcData: DecontaminationCalculation['contaminantDeadOilCalcData'];
  contaminantLiveOilCalcData: DecontaminationCalculation['contaminantLiveOilCalcData'];
  deadLiveCalcData: DeadLiveCalcData[];
  flashedSampleTable: ContaminationFlashedCalcData[];
  deadLiveSampleTable: DeadLiveCalcData[];
}

export interface DecontaminationChartAxisRange {
  xMin: number;
  xMax: number;
  xStep: number;
  yMin: number;
  yMax: number;
  yStep: number;
}

@Injectable({
  providedIn: 'root'
})
export class DecontaminationService {
  private http = inject(HttpClient);


  private apiBaseUrl = environment.apiUrl+apiV1;
  public statusChange$ = new BehaviorSubject<StatusParamsDecont>({ statusId: NC.zero, statusName: PC.emptyString });

  GetDecontamination(labTestRepeatId: number) {
    const params = new HttpParams().set(PC.testRepeatId, labTestRepeatId.toString());
    return this.http.get<APIResponse<DecontaminationSaveData>>(`${this.apiBaseUrl}${AE.GETDECONTAMINATION}`, {params});
  }
  SaveDecontamination(data:DecontaminationSaveData) {
    return this.http.post<APIResponse<DecontaminationSaveData>>(`${this.apiBaseUrl}${AE.SAVEDECONTAMINATION}`, data);
  }
  UpdateDecontamination(data: DecontaminationSaveData) {
    return this.http.post<APIResponse<DecontaminationSaveData>>(`${this.apiBaseUrl}${AE.UPDATEDECONTAMINATION}`, data);
  }
  PartialSaveDecontamination(data: DecontaminationSaveData) {
    return this.http.post<APIResponse<DecontaminationSaveData>>(`${this.apiBaseUrl}${AE.PARTIALSAVEDECONTAMINATION}`, data);
  }
  GetFinalReportDecontamination(testRepeatId: number) {
    const params = new HttpParams().set(PC.testRepeatId, testRepeatId.toString());
    return this.http.get<APIResponse<unknown>>(`${this.apiBaseUrl}${AE.GETFINALREPORTDECONTAMINATION}`, {params});
  }

  UpdateStatus(status: StatusParamsDecont) {
    this.statusChange$.next(status);
  }

  BuildCalculationViewState(calculation: DecontaminationCalculation): DecontaminationCalculationViewState {
    const contaminationFlashedCalcData = calculation.contaminationFlashedCalcData || [];
    const deadLiveCalcData = calculation.deadLiveCalcData || [];

    return {
      contaminantPropertiesCalcData: calculation.contaminantPropertiesCalcData,
      contaminationFlashedCalcData,
      contaminantDeadOilCalcData: calculation.contaminantDeadOilCalcData || [],
      contaminantLiveOilCalcData: calculation.contaminantLiveOilCalcData || [],
      deadLiveCalcData,
      flashedSampleTable: contaminationFlashedCalcData,
      deadLiveSampleTable: deadLiveCalcData,
    };
  }

  CanRenderDecontaminationCharts(
    flashedSampleTable: ContaminationFlashedCalcData[] | undefined,
    deadLiveSampleTable: DeadLiveCalcData[] | undefined,
  ): boolean {
    return flashedSampleTable !== undefined && deadLiveSampleTable !== undefined;
  }

  BuildChartAxisRange(
    xMin: number,
    xMax: number,
    xStep: number,
    yMin: number,
    yMax: number,
    yStep: number,
  ): DecontaminationChartAxisRange {
    return {
      xMin,
      xMax,
      xStep,
      yMin,
      yMax,
      yStep,
    };
  }

  GetDefaultResidualOilAxisRange(): DecontaminationChartAxisRange {
    return this.BuildChartAxisRange(
      NC.five,
      NC.twentyFive,
      NC.five,
      NC.zero,
      NC.eight,
      NC.one,
    );
  }

  GetDefaultLiveOilAxisRange(): DecontaminationChartAxisRange {
    return this.BuildChartAxisRange(
      NC.five,
      NC.twentyFive,
      NC.five,
      NC.zero,
      NC.eight,
      NC.one,
    );
  }

  FormatDeadContamMass(value: number | null | undefined, component?: string): string {
    if (value === null || value === undefined || value === NC.zero) {
      return NC.zeroString;
    }

    if (component === CC.c36Plus) {
      return value.toString();
    }

    if (Math.abs(value) < NC.smallValueThreshold) {
      return value.toExponential(NC.five).toUpperCase();
    }

    return value.toString();
  }

  GetPreviousTabIndex(selectedIndex: number, tabCount: number): number {
    if (selectedIndex <= NC.zero) {
      return NC.zero;
    }

    return (selectedIndex - NC.one) % tabCount;
  }

  GetNextTabIndex(selectedIndex: number, tabCount: number): number {
    return (selectedIndex + NC.one) % tabCount;
  }

  BuildResidualOilDatasets(
    flashedSampleTable: ContaminationFlashedCalcData[],
    deadLiveSampleTable: DeadLiveCalcData[],
  ): ScatterDataset[] {
    const contaminatedFluidGraphData = this.GetChartData(
      flashedSampleTable,
      DC.calculatedFlashedLiquidWtPct
    );
    const contaminantGraphData = this.GetChartData(
      flashedSampleTable,
      DC.calculatedContaminantCompositionWt
    );
    const deContaminatedGraphData = this.GetChartData(
      deadLiveSampleTable,
      DC.calculatedDeadUncontWtPct
    );

    return [
      {
        label: DC.contaminatedFluid,
        data: contaminatedFluidGraphData,
        borderColor: '#5B9BD5',
        backgroundColor: '#5B9BD5',
        showLine: true,
        fill: false,
        borderWidth: NC.one,
        pointStyle: 'circle',
        pointRadius: NC.two
      },
      {
        label: RPC.contaminant,
        data: contaminantGraphData,
        borderColor: '#ED7D31',
        backgroundColor: '#ED7D31',
        showLine: true,
        fill: false,
        borderWidth: NC.one,
        pointStyle: 'circle',
        pointRadius: NC.two
      },
      {
        label: DC.deContaminated,
        data: deContaminatedGraphData,
        borderColor: '#c7c8c9ff',
        backgroundColor: '#c7c8c9ff',
        showLine: true,
        fill: false,
        borderWidth: NC.one,
        pointStyle: 'circle',
        pointRadius: NC.two
      }
    ];
  }

  BuildLiveOilDatasets(
    flashedSampleTable: ContaminationFlashedCalcData[],
    deadLiveSampleTable: DeadLiveCalcData[],
  ): ScatterDataset[] {
    const contaminatedWholeFluidData = this.GetChartData(
      flashedSampleTable,
      DC.calculatedWholeFluidWtPct
    );
    const contaminantData = this.GetChartData(
      flashedSampleTable,
      DC.calculatedContaminantCompositionWt
    );
    const liveOilDeContaminatedData = this.GetChartData(
      deadLiveSampleTable,
      DC.calculatedLiveUncontWtPct
    );

    return [
      {
        label: DC.contaminatedFluid,
        data: contaminatedWholeFluidData,
        borderColor: '#5B9BD5',
        backgroundColor: '#5B9BD5',
        showLine: true,
        fill: false,
        borderWidth: NC.one,
        pointStyle: 'circle',
        pointRadius: NC.two
      },
      {
        label: RPC.contaminant,
        data: contaminantData,
        borderColor: '#ED7D31',
        backgroundColor: '#ED7D31',
        showLine: true,
        fill: false,
        borderWidth: NC.one,
        pointStyle: 'circle',
        pointRadius: NC.two
      },
      {
        label: DC.deContaminated,
        data: liveOilDeContaminatedData,
        borderColor: '#c7c8c9ff',
        backgroundColor: '#c7c8c9ff',
        showLine: true,
        fill: false,
        borderWidth: NC.one,
        pointStyle: 'circle',
        pointRadius: NC.two
      }
    ];
  }

  CreateResidualOilChart(
    ctx: CanvasRenderingContext2D,
    flashedSampleTable: ContaminationFlashedCalcData[],
    deadLiveSampleTable: DeadLiveCalcData[],
    axisRange: DecontaminationChartAxisRange,
  ): Chart {
    return this.CreateChart(
      ctx,
      this.BuildResidualOilDatasets(flashedSampleTable, deadLiveSampleTable),
      DC.residualOil,
      DC.cSharp,
      DC.wtPercent,
      axisRange.xMin,
      axisRange.xMax,
      axisRange.xStep,
      axisRange.yMin,
      axisRange.yMax,
      axisRange.yStep,
    );
  }

  CreateLiveOilChart(
    ctx: CanvasRenderingContext2D,
    flashedSampleTable: ContaminationFlashedCalcData[],
    deadLiveSampleTable: DeadLiveCalcData[],
    axisRange: DecontaminationChartAxisRange,
  ): Chart {
    return this.CreateChart(
      ctx,
      this.BuildLiveOilDatasets(flashedSampleTable, deadLiveSampleTable),
      DC.liveOil,
      DC.cSharp,
      DC.wtPercent,
      axisRange.xMin,
      axisRange.xMax,
      axisRange.xStep,
      axisRange.yMin,
      axisRange.yMax,
      axisRange.yStep,
    );
  }

  BuildDecontaminationViewState(result: DecontaminationSaveData | undefined, wellName: string): DecontaminationViewState {
    const decontaminationInputModel: DecontaminationInputModel = {
      flashedData: this.FilterAndRenameC36Plus(result?.flashedData || []),
      propertiesData: result?.propertiesData ?? {},
      contaminantData: result?.contaminantData ?? {}
    };

    if (decontaminationInputModel.contaminantData?.contaminantComposition) {
      decontaminationInputModel.contaminantData.contaminantComposition =
        this.ReplaceAndRemoveC36Plus(decontaminationInputModel.contaminantData.contaminantComposition);
    }

    const decontaminationCalculation: DecontaminationCalculation = {
      contaminantPropertiesCalcData: result?.contaminantPropertiesCalcData,
      contaminationFlashedCalcData: this.FilterUpToC36(result?.contaminationFlashedCalcData || [], x => x.componentCalc),
      contaminantDeadOilCalcData: this.FilterUpToC36(result?.contaminantDeadOilCalcData || [], x => x.component),
      contaminantLiveOilCalcData: this.FilterUpToC36(result?.contaminantLiveOilCalcData || [], x => x.component),
      deadLiveCalcData: this.FilterUpToC36(result?.deadLiveCalcData || [], x => x.componentCalc),
    };

    const showResult = result?.contaminantPropertiesCalcData?.liveOilContWtPct !== null
      && result?.contaminantPropertiesCalcData?.liveOilContWtPct !== undefined;

    const decontaminationResult: DecontaminationResult = {
      vw_DecontaminationProperties: result?.vw_DecontaminationProperties ?? {} as Vw_DecontaminationPropertiesData,
      vw_DecontaminationUncontaminated: result?.vw_DecontaminationUncontaminated || [],
      vw_DecontaminationContaminated: result?.vw_DecontaminationContaminated ?? {} as Vw_DecontaminationContaminatedData,
      flashedData: result?.flashedData || [],
      propertiesData: result?.propertiesData ?? {},
      contaminantData: result?.contaminantData ?? {},
      sampleName: wellName,
    };

    return {
      decontaminationInputModel,
      decontaminationCalculation,
      decontaminationResult,
      showResult,
      dataLoaded: showResult,
    };
  }

  GetDecontaminationViewState(labTestRepeatId: number, wellName: string) {
    return this.GetDecontamination(labTestRepeatId).pipe(
      map((res) => {
        if (!res?.isSuccess) {
          return null;
        }

        return this.BuildDecontaminationViewState(res.result, wellName);
      })
    );
  }

  FilterAndRenameC36Plus(data: ContaminationFlashedData[]): ContaminationFlashedData[] {
    const c36Index = data.findIndex(item => item.component === RC.cThirtySix || item.component === CC.c36Plus);

    if (c36Index === NC.n1Neg) {
      return data;
    }

    let filtered = data.slice(NC.zero, c36Index + NC.one);
    filtered = filtered.map(item => {
      if (item.component === RC.cThirtySix || item.component === CC.c36Plus) {
        return { ...item, component: CC.c36Plus };
      }
      return item;
    });
    return filtered;
  }

  ReplaceAndRemoveC36Plus(compositions: ContaminantComposition[]): ContaminantComposition[] {
    return compositions.map(item => {
      if (item.component === CC.c36Plus || item.component === RC.cThirtySix) {
        return {
          contaminantCompositionId: item.contaminantCompositionId,
          componentId: item.componentId,
          contaminantCompositionWt: item.contaminantCompositionWt,
          component: CC.c36
        };
      }
      return item;
    });
  }

  FilterUpToC36<T>(data: T[], getComponent: (item: T) => string): T[] {
    const c36Index = data.findIndex(item => {
      const comp = getComponent(item);
      return comp === RC.cThirtySix || comp === CC.c36Plus;
    });

    const totalIndex = data.findIndex(item => getComponent(item).toLowerCase() === RC.totals);

    if (c36Index === NC.n1Neg) {
      return data;
    }

    const filtered = data.slice(NC.zero, c36Index + NC.one);

    if (totalIndex !== NC.n1Neg && totalIndex > c36Index) {
      filtered.push(data[totalIndex]);
    }
    return filtered;
  }
 
  GetChartData(
    source: { componentCalc: string }[],
    yKey: string
  ) {
    return source
      .filter(item => item.componentCalc !== CC.totals)
      .map(item => ({
        x: Number(item.componentCalc.replace(RPC.c, PC.emptyString).replace('+', PC.emptyString)),
        y: Number((item as Record<string, number | string>)[yKey]),
        compound: item.componentCalc
      }));
  }

  GetDynamicYMax(flashedSampleTable: ContaminationFlashedCalcData[],deadLiveSampleTable: DeadLiveCalcData[],
    flashedKeys: (keyof ContaminationFlashedCalcData)[],deadLiveKeys: (keyof DeadLiveCalcData)[] ): number {
    const flashedY = flashedKeys.flatMap(key =>
      flashedSampleTable
        .filter(item => item.componentCalc !== CC.totals)
        .map(item => Number(item[key]))
        .filter(y => !isNaN(y))
    );
    const deadLiveY = deadLiveKeys.flatMap(key =>
      deadLiveSampleTable
        .filter(item => item.componentCalc !== CC.totals)
        .map(item => Number(item[key]))
        .filter(y => !isNaN(y))
    );
    return Math.max(...flashedY, ...deadLiveY);
  }


  CreateChart(
    ctx: CanvasRenderingContext2D,
    datasets: ScatterDataset[],
    plotTitle: string,
    xAxisTitle: string,
    yAxisTitle: string,
    xMin: number,
    xMax: number,
    xStep: number,
    yMin: number,
    yMax: number,
    yStep: number
  ) {
    return new Chart(ctx, {
      type: 'scatter',
      data: { datasets },
      options: {
        plugins: {
          title: { display: true, text: plotTitle, align: 'center' },
          legend: { labels: { usePointStyle: true }, position: 'right' },
          datalabels: { color: '#000000', align: 'bottom', formatter: () => PC.emptyString },
          zoom: {
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' },
            pan: { enabled: true, mode: 'xy' }
          }
        },
        scales: {
          x: {
            type: 'linear',
            beginAtZero: false,
            min: xMin,
            max: xMax,
            position: 'bottom',
            grid: { display: false },
            ticks: { color: '#000000', font: { weight: 'bold' }, major: { enabled: true }, stepSize: xStep },
            title: { display: true, text: xAxisTitle }
          },
          y: {
            type: 'linear',
            beginAtZero: false,
            min: yMin,
            max: yMax,
            ticks: { major: { enabled: true }, stepSize: yStep, color: '#000000', font: { weight: 'bold' } },
            grid: { display: false },
            title: { display: true, text: yAxisTitle }
          }
        }
      }
    });
  }

  CreateResultChart(
    ctx: CanvasRenderingContext2D,
    datasets: ScatterDataset[],
    plotTitle: string,
    xAxisTitle: string,
    yAxisTitle: string, onComplete?: () => void
  ) {
    return new Chart(ctx, {
      type: 'scatter',
      data: { datasets },
      options: {
        plugins: {
          title: { display: true, text: plotTitle, align: 'center' },
          legend: { labels: { usePointStyle: true }, position: 'right' },
          datalabels: { color: '#000000', align: 'bottom', formatter: () => PC.emptyString },
          zoom: {
            zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'xy' },
            pan: { enabled: true, mode: 'xy' }
          }
        },
        scales: {
          x: {
            type: 'linear',
            beginAtZero: false,
            min: NC.five,
            max: NC.twentyFive,
            position: 'bottom',
            grid: { display: false },
            ticks: { color: '#000000', font: { weight: 'bold' }, major: { enabled: true }, stepSize: NC.five },
            title: { display: true, text: xAxisTitle }
          },
          y: {
            type: 'linear',
            beginAtZero: false,
            min: NC.zero,
            max: NC.eight,
            ticks: { major: { enabled: true }, stepSize: NC.one, color: '#000000', font: { weight: 'bold' } },
            grid: { display: false },
            title: { display: true, text: yAxisTitle }
          }
        },
        animation: onComplete ? { onComplete: () => onComplete() } : undefined
      }
    });
  }
  
  CreateChartdecontaminationReport(bubbletableData: DeContaminationChart[], ctxPO1: ChartItem, ctxPO2: ChartItem) {
      const contaminatedFluidGraphData = [{}];
      const contaminantGraphData = [{}];
      const deContaminatedGraphData = [{}];
      const contaminatedWholeFluidData = [{}];
      const liveOilDeContaminatedData = [{}];
      for (let n = NC.zero; bubbletableData.length > n; n++) {
        contaminatedFluidGraphData.push({ x: bubbletableData[n].contaminatedFluid_X, y: bubbletableData[n].contaminatedFluid_Y })
        contaminantGraphData.push({ x: bubbletableData[n].contaminant_X, y: bubbletableData[n].contaminant_Y })
        deContaminatedGraphData.push({ x: bubbletableData[n].deContaminated_X, y: bubbletableData[n].deContaminated_Y })
        contaminatedWholeFluidData.push({ x: bubbletableData[n].deContaminatedData_X, y: bubbletableData[n].deContaminatedData_Y })
        liveOilDeContaminatedData.push({ x: bubbletableData[n].liveOilDeContaminatedData_X, y: bubbletableData[n].liveOilDeContaminatedData_Y })
      }
      //For Dead Oil chart
      const deadOil_Chart = new Chart(ctxPO1, {
        type: 'scatter',
        data: {
          datasets: [
            {
              label: DC.decontaminated,
              data: deContaminatedGraphData,
              borderColor: '#000',
              backgroundColor: '#c7c8c9',
              pointBorderColor: '#000',
              pointBackgroundColor: '#c7c8c9',
              pointRadius: NC.fourteen,
              pointBorderWidth: NC.three,
              showLine: true,
              fill: false,
              borderWidth: NC.three,
            },
            {
              label: RPC.contaminant,
              data: contaminantGraphData,
              borderColor: '#000',
              backgroundColor: '#ED7D31',
              pointBorderColor: '#000',
              pointBackgroundColor: '#ED7D31',
              pointRadius: NC.sixteen,
              pointBorderWidth: NC.three,
              showLine: true,
              fill: false,
              borderWidth: NC.three,
            },
            {
              label: DC.contaminatedFluid,
              data: contaminatedFluidGraphData,
              borderColor: '#000',
              backgroundColor: '#5B9BD5',
              pointBorderColor: '#000',
              pointBackgroundColor: '#5B9BD5',
              pointRadius: NC.sixteen,
              pointBorderWidth: NC.three,
              showLine: true,
              fill: false,
              borderWidth: NC.three,
            }       
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
            datalabels: { display: false },
            customLegendAndTitle: {
              titleText: PVC.DeadOil
            }
          },
          animation: false,
          scales: {
            x: {
              title: {
                display: true,
                text: DC.carbonNumber,
                font: { size: NC.sixtySix, weight: 'bold' },
                color: '#000',
              },
              ticks: {
                padding: NC.twentyFive,
                font: { size: NC.thirtyEight, weight: 'bold' },
                color: '#000',
                stepSize: NC.five,
              },
              min: NC.five,
              max: NC.twentyFive,
              grace: DC.fivePercent,
              grid: { display: false },
              border: { color: '#000', z: NC.ten }
            },
            y: {
              title: {
                display: true,
                text: DC.wtPercent,
                font: { size: NC.sixtySix, weight: 'bold' },
                color: '#000'
              },
              type: 'linear',
              position: 'bottom',
              ticks: {
                padding: NC.twentyFive,
                font: { size: NC.thirtyEight, weight: 'bold' },
                color: '#000',
                stepSize: NC.one
              },
              min: NC.zero,
              max: NC.eight,
              grid: { display: false },
              border: { color: '#000', z: NC.ten }
            }
          }
        },
        plugins: [this.fullBorderPlugin, this.customLegendAndTitle, this.axisOriginTicksPlugin, this.legendLineThroughCirclePlugin, this.pointsAboveAxesPlugin]
      });

      //For Live Oil chart
      const liveOil_Chart = new Chart(ctxPO2, {
        type: 'scatter',
        data: {
          datasets: [
            {
              label: DC.decontaminated,
              data: liveOilDeContaminatedData,
              borderColor: '#000',
              backgroundColor: '#c7c8c9',
              pointBorderColor: '#000',
              pointBackgroundColor: '#c7c8c9',
              pointRadius: NC.sixteen,
              pointBorderWidth: NC.three,
              showLine: true,
              fill: false,
              borderWidth: NC.three,
              pointStyle: 'circle',
            },
            {
              label: RPC.contaminant,
              data: contaminantGraphData,
              borderColor: '#000',
              backgroundColor: '#ED7D31',
              pointBorderColor: '#000',
              pointBackgroundColor: '#ED7D31',
              pointRadius: NC.sixteen,
              pointBorderWidth: NC.three,
              showLine: true,
              fill: false,
              borderWidth: NC.three,
              pointStyle: 'circle',
            },
            {
              label: DC.contaminatedFluid,
              data: contaminatedWholeFluidData,
              borderColor: '#000',
              backgroundColor: '#5B9BD5',
              pointBorderColor: '#000',
              pointBackgroundColor: '#5B9BD5',
              pointRadius: NC.sixteen,
              pointBorderWidth: NC.three,
              showLine: true,
              fill: false,
              borderWidth: NC.three,
              pointStyle: 'circle'
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
            datalabels: { display: false },
            customLegendAndTitle: {
              titleText: RPC.liveFluid
            }
          },
          animation: false,
          scales: {
            x: {
              title: {
                display: true,
                text: DC.carbonNumber,
                font: { size: NC.fiftySix, weight: 'bold' },
                color: '#000'
              },
              ticks: {
                padding: NC.twentyFive,
                font: { size: NC.thirtyEight, weight: 'bold' },
                color: '#000',
                stepSize: NC.five
              },
              min: NC.five,
              max: NC.twentyFive,
              grace: DC.fivePercent,
              grid: { display: false },
              border: { color: '#000', z: NC.ten }
            },
            y: {
              title: {
                display: true,
                text: 'Wt%',
                font: { size: NC.fiftySix, weight: 'bold' },
                color: '#000'
              },
              ticks: {
                padding: NC.twentyFive,
                font: { size: NC.thirtyEight, weight: 'bold' },
                color: '#000',
                stepSize: NC.one
              },
              min: NC.zero,
              max: NC.eight,
              grid: { display: false },
              border: { color: '#000', z: NC.ten } // Axis line on top
            }
          }
        },
        plugins: [this.fullBorderPlugin, this.customLegendAndTitle, this.axisOriginTicksPlugin, this.legendLineThroughCirclePlugin, this.pointsAboveAxesPlugin]
      });
      
      const deadOilImage = deadOil_Chart.toBase64Image();
      const liveOilImage = liveOil_Chart.toBase64Image();

      return [deadOilImage, liveOilImage];
  }

  boxBorderPlugin: ChartJsPlugin<'scatter'> = {
    id: 'boxBorderPlugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      ctx.save();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = NC.three;

      // Top border
      ctx.beginPath();
      ctx.moveTo(chartArea.left, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.top);
      ctx.stroke();

      // Right border
      ctx.beginPath();
      ctx.moveTo(chartArea.right, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.bottom);
      ctx.stroke();

      ctx.restore();
    }
  };
  axisOriginTicksPlugin: ChartJsPlugin<'scatter'> = {
    id: 'axisOriginTicksPlugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const xScale = chart.scales['x'];
      const yScale = chart.scales['y'];
      const tickLength = NC.ten;
      ctx.save();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = NC.two;
      // Draw ticks on X axis (inside plot area only)
      xScale.ticks.forEach((tick) => {
        const x = xScale.getPixelForValue((tick as { value: number }).value);
        const yEnd = chart.chartArea.bottom;

        if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
          ctx.beginPath();
          ctx.moveTo(x, yEnd - tickLength); // bottom edge inward
          ctx.lineTo(x, yEnd);              // bottom edge
          ctx.stroke();
        }
      });

      // Draw ticks on Y axis (inside plot area only)
      yScale.ticks.forEach((tick) => {
        const y = yScale.getPixelForValue((tick as { value: number }).value);
        const xStart = chart.chartArea.left;

        if (y >= chart.chartArea.top && y <= chart.chartArea.bottom) {
          ctx.beginPath();
          ctx.moveTo(xStart, y);              // left edge
          ctx.lineTo(xStart + tickLength, y); // left edge inward
          ctx.stroke();
        }
      });

      ctx.restore();

    }
  };

  fullBorderPlugin: ChartJsPlugin<'scatter'> = {
    id: 'fullBorderPlugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const area = chart.chartArea;
      ctx.save();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = NC.three;
      ctx.beginPath(); ctx.moveTo(area.left, area.top); ctx.lineTo(area.right, area.top); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(area.right, area.top); ctx.lineTo(area.right, area.bottom); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(area.right, area.bottom); ctx.lineTo(area.left, area.bottom); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(area.left, area.bottom); ctx.lineTo(area.left, area.top); ctx.stroke();
      ctx.restore();
    }
  };

  customLegendAndTitle: ChartJsPlugin<'scatter', { titleText?: string }> = {
    id: 'customLegendAndTitle',
    afterDraw: (chart, _args, options) => {
      const ctx = chart.ctx;
      const { top, right } = chart.chartArea;
      ctx.save();
      ctx.font = 'bold 44px Arial';
      ctx.fillStyle = '#000';
      // Use dynamic title
      const title = options?.titleText ?? PVC.DeadOil;
      ctx.fillText(title, chart.chartArea.left + NC.ten, top + NC.eighty);
      // Legend
      const legendX = right - NC.fourFifty;
      const legendY = top + NC.eighty;
      const legendItems = [
        { color: '#5B9BD5', text: 'Contaminated Fluid' },
        { color: '#ED7D31', text: 'Contaminant' },
        { color: '#c7c8c9', text: 'Decontaminated' }
      ];
      ctx.font = 'bold 44px Arial';
      legendItems.forEach((item, i) => {
        const y = legendY + i * NC.fifty;
        ctx.strokeStyle = '#000';
        ctx.lineWidth = NC.two;
        ctx.beginPath();
        ctx.arc(legendX, y, NC.twelve, NC.zero, Math.PI * NC.two);
        ctx.fillStyle = item.color;
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#000';
        ctx.fillText(item.text, legendX + NC.thirty, y + NC.eight);
      });
      ctx.restore();
    }
  };
    
  legendLineThroughCirclePlugin: ChartJsPlugin<'scatter'> = {
    id: 'legendLineThroughCircle',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const legend = chart.legend;

      if (!legend) return;

      const font = toFont(legend.options.labels.font as Partial<FontSpec>);
      const fontSize = font.size;

      const baseOffset = NC.eighty;
      const lineSpacing = NC.fifty;

      legend.legendItems?.forEach((_item, index) => {
        const boxWidth = legend.options.labels.boxWidth;
        const boxHeight = legend.options.labels.boxHeight || fontSize;
        const x = legend.right - NC.fiveOneEight; // approximate position
        const y = legend.top + baseOffset + index * lineSpacing;
        const spacing = NC.four;
        const lineY = y + boxHeight / NC.six;

        ctx.save();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = NC.two;
        ctx.beginPath();
        ctx.moveTo(x - spacing, lineY);
        ctx.lineTo(x + boxWidth + spacing, lineY);
        ctx.stroke();
        ctx.restore();
      });
    }
  };

  pointsAboveAxesPlugin = {
    id: 'pointsAboveAxes',
    afterDraw(chart: Chart) {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      // Expand clipping area by the maximum point radius
      const padding = NC.twenty; // Adjust based on your largest pointRadius
      ctx.save();
      ctx.beginPath();
      ctx.rect(
        chartArea.left - padding,
        chartArea.top,
        chartArea.right - chartArea.left + padding * NC.two,
        chartArea.bottom - chartArea.top + padding
      );
      ctx.clip();

      // Redraw points
      chart.data.datasets.forEach((_, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta.hidden) {
          meta.controller.draw();
        }
      });

      ctx.restore();
    }
  };

}
